/**
 * Supabase Configuration
 * Centralizes all Supabase-related configurations
 */

export interface SupabaseConfig {
  url: string;
  anonKey: string;
}

export type RegistryConfig = SupabaseConfig;

export interface OrganizationConfig extends SupabaseConfig {
  slug: string;
}

/**
 * Gets the Supabase configuration for the Registry from environment variables
 * @throws {Error} If registry credentials are missing
 */
export const getRegistryConfig = (): RegistryConfig => {
  const url = import.meta.env.VITE_REGISTRY_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_REGISTRY_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    console.error(
      "Registry credentials are required: VITE_REGISTRY_SUPABASE_URL and VITE_REGISTRY_SUPABASE_ANON_KEY"
    );
    console.error("Please create a .env.local file with your Supabase registry credentials");
    console.error("Example:");
    console.error("VITE_REGISTRY_SUPABASE_URL=https://your-registry-project.supabase.co");
    console.error("VITE_REGISTRY_SUPABASE_ANON_KEY=your_registry_anon_key_here");
    
    throw new Error(
      "Registry credentials are required: VITE_REGISTRY_SUPABASE_URL and VITE_REGISTRY_SUPABASE_ANON_KEY"
    );
  }

  return { url, anonKey };
};

/**
 * Retry configuration for database operations
 */
export const RETRY_CONFIG = {
  maxAttempts: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 1.5,
} as const;

/**
 * Local storage configuration
 */
export const STORAGE_KEYS = {
  currentOrganizationSlug: "currentOrganizationSlug",
  /** Prefix for per-slug cached org Supabase credentials (offline cold start). */
  organizationCredentialsPrefix: "orgConnectionCredentials",
} as const;
